import * as UpChunk from "@mux/upchunk";
import { useNavigation } from "@react-navigation/native";
import * as ImagePicker from "expo-image-picker";
import { useCallback, useState } from "react";

import {
  Button,
  Col1,
  Form,
  Icon,
  Row,
  Spinner,
  TextInput,
  TextTitle,
} from "../lib";
import {
  useUserVideoCreate,
  useUserStatusCreate,
  useUserStatuses,
  useUserVideos,
} from "../services";
import { createStyles, sizes } from "../theme";
import { LoadingScreen } from "./loading";
import { Callout } from "../components";
import { ImageBackground } from "expo-image";
import { Pressable } from "react-native";

export function TimelineCreateScreen() {
  // Data
  const { invalidate: invalidateStatuses } = useUserStatuses();
  const { invalidate: invalidateVideos } = useUserVideos();

  // Vars
  const navigation = useNavigation();

  // Services
  const [createVideo] = useUserVideoCreate();
  const [createStatus] = useUserStatusCreate();

  // State
  const [asset, setAsset] = useState<ImagePicker.ImagePickerAsset | null>(null);
  const [description, setDescription] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  // Methods
  const uploadVideo = useCallback(
    async (asset: ImagePicker.ImagePickerAsset) => {
      try {
        setLoading(true);

        const data = await createVideo();
        const videoResponse = await fetch(asset.uri);
        const videoBlob = await videoResponse.blob();

        try {
          setUploadProgress(0);

          await new Promise<void>((resolve, reject) => {
            const upload = UpChunk.createUpload({
              endpoint: data.uploadUrl,
              file: asset.file!,
              chunkSize: 5120, // Uploads the file in ~5mb chunks
            });

            // Subscribe to events
            upload.on("error", (error) => {
              reject(error.detail);
            });

            upload.on("progress", (progress) => {
              setUploadProgress(Math.round(progress.detail));
            });

            upload.on("success", () => {
              resolve();
            });
          });
        } catch (err) {
          console.error("TUS upload failed:", err);

          setUploadProgress(null);

          await fetch(data.uploadUrl, {
            method: "PUT",
            body: videoBlob,
            headers: { "content-type": videoBlob.type },
          });
        }

        invalidateVideos();

        navigation.goBack();
      } catch (err) {
        console.error("FETCH upload failed:", err);

        setAsset(null);
        setLoading(false);
      }
    },
    [createVideo, invalidateVideos, navigation],
  );

  const handlePickerResult = useCallback(
    async (result: ImagePicker.ImagePickerResult) => {
      if (!result.canceled) {
        const asset = result.assets[0] ?? null;

        setAsset(asset);

        if (asset?.type === "video") {
          uploadVideo(asset);
        }
      }
    },
    [uploadVideo],
  );

  const pickCamera = useCallback(async () => {
    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: ["images", "videos"],
    });

    handlePickerResult(result);
  }, [handlePickerResult]);

  const pickUpload = useCallback(async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images", "videos"],
      allowsMultipleSelection: false,
    });

    handlePickerResult(result);
  }, [handlePickerResult]);

  // Handlers
  const handleChangeDescription = useCallback(
    (_name: "description", text: string | null) => {
      setDescription(text ?? "");
    },
    [],
  );

  const handleSubmitStatus = useCallback(async () => {
    if (description && asset?.file) {
      setLoading(true);

      try {
        const data = new FormData();

        data.append("image", asset.file);
        data.append("mime", asset.file.type);
        data.append(
          "size",
          Math.round((asset.fileSize || 0) / 1000).toString(),
        );
        data.append("description", description);

        await createStatus(data);

        invalidateStatuses();
        navigation.goBack();
      } catch {
        // ignore
      }
    }
  }, [
    asset?.file,
    asset?.fileSize,
    createStatus,
    description,
    invalidateStatuses,
    navigation,
  ]);

  // Render
  if (!asset) {
    return (
      <Callout
        title="Pick asset"
        message="Upload an existing file or use the camera"
      >
        <Row.C mt="6" style={$.container}>
          <Button icon="camera" onPress={pickCamera}>
            Use camera
          </Button>
          <Button icon="upload" onPress={pickUpload}>
            Upload file
          </Button>
        </Row.C>
      </Callout>
    );
  }

  if (asset.type === "video" && isLoading) {
    return (
      <LoadingScreen title="Uploading video...">
        {!!uploadProgress && <TextTitle>{uploadProgress}%</TextTitle>}
      </LoadingScreen>
    );
  }

  return (
    <Form>
      <ImageBackground source={{ uri: asset.uri }} style={$.image}>
        <Row.LR>
          <Col1 mr="2">
            <TextInput
              name="description"
              placeholder="Add caption..."
              onChange={handleChangeDescription}
              value={description}
              disabled={isLoading}
            />
          </Col1>
          <Pressable disabled={isLoading} onPress={handleSubmitStatus}>
            {isLoading ? (
              <Spinner large color="onOverlay" />
            ) : (
              <Icon large name="arrow-right" color="onOverlay" />
            )}
          </Pressable>
        </Row.LR>
      </ImageBackground>
    </Form>
  );
}

const $ = createStyles({
  container: {
    gap: sizes["2"],
  },

  image: {
    flex: 1,
    height: undefined,
    width: undefined,
    p: "6",
    flexDirection: "column",
    justifyContent: "flex-end",
  },
});
